import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Form, Input, InputNumber, Row, Col } from 'antd';
import { useSelector } from 'react-redux';
import { SelectPriority } from './SelectPriority';
import { FormRepeatAppointments } from './repeatAppointments/FormRepeatAppointments';
import { ModalTechniciansNear } from './modalTechniciansNear/ModalTechniciansNear';
import { SelectCompany } from '../../../components/services/forms/SelectCompany';
import { SelectContact } from '../../../components/services/forms/SelectContact';
import { SelectServiceType } from '../../../components/services/forms/SelectServiceType';
import { SelectUser } from '../../../components/users/SelectUser';
import { profileTypes } from '../../../config/constants/userConstants';
import { FinalCustomerInput } from '../../../components/finalCustomers/FinalCustomerInput/FinalCustomerInput';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { SelectHeadquarters } from '../../../components/headquarters/SelectHeadquarters';
import { servicePriorities } from '../../../config/constants/servicePrioritiesConstants';
import { incidentsStatus } from '../../../config/constants/incidentsStatus';
import { CustomSwitch } from '../../SelfScheduling/components/form/editScheduleBlocks/CustomSwitch';
import useModalFieldsVerification from '../../../components/ModalFieldsVerification/hooks/useModalFieldsVerification';
import { GooglePlacesAutocomplete } from '../../../components/geolocation/GooglePlacesAutocomplete';
import { AssetsByFinalCustomerButtons } from '../../../components/assetsInput/AssetsByFinalCustomerButtons';
import {
  defaultServiceProfileRules,
  serviceSolutionRules,
} from '../../../config/constants/serviceSolutionRulesConstants';

export const FormEditService = ({
  isEditing,
  formName,
  serviceData,
  handleSubmit: onSubmit,
  onChangeValues,
  selectedProfile,
}) => {
  const [form] = Form.useForm();
  const { translate: t } = useDictionaryTranslation();
  const { verifyFields } = useModalFieldsVerification();
  const selectedHeadquarter = Form.useWatch('headquarter', form);
  const selectedServiceType = Form.useWatch('serviceType', form);
  const selectedTechnician = Form.useWatch('selectedTechnician', form);
  const collaborators = Form.useWatch('collaborators', form);
  const selectedFinalCustomer = Form.useWatch('selectedFinalCustomer', form);
  const selectedCompany = Form.useWatch('assignedCompany', form);
  const serviceDate = Form.useWatch('serviceDate', form);
  const isScheduled = Form.useWatch('isScheduled', form);
  const showScheduledSection =
    Boolean(selectedServiceType?.enableScheduledServices) &&
    (!isEditing || serviceData?.status === incidentsStatus.SCHEDULED);
  const { accountModules, solutionId, currentUser } = useSelector((state) => ({
    accountModules: state.auth.accountModules,
    solutionId: state.auth.accountData.solutionId,
    currentUser: state.auth.currentUser,
  }));
  const [switchappointmentsRepeat, setSwitchappointmentsRepeat] = useState(false);
  const [showModalTechniciansNear, setShowModalTechniciansNear] = useState(false);

  const currentSolutionRules = serviceSolutionRules[solutionId] ?? serviceSolutionRules.default;
  const selectedProfileRules =
    currentSolutionRules.profileRules?.[selectedProfile] ??
    currentSolutionRules.profileRules?.default ??
    defaultServiceProfileRules.default;

  const validateTime = (_, time) => {
    if (serviceData?.start) {
      const currentServiceDate = moment(serviceData.start);
      if (serviceDate === currentServiceDate.format('YYYY-MM-DD') && time === currentServiceDate.format('HH:mm')) {
        return Promise.resolve();
      }
    }
    const today = moment();
    const todaysDate = today.format('YYYY-MM-DD');
    if (serviceDate > todaysDate) {
      return Promise.resolve();
    }
    const minTime = today.format('HH:mm');
    if (serviceDate === todaysDate && time > minTime) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t('minTimeError', { minTime })));
  };

  const onHandleCollaborators = (items) => {
    const data = items?.map((collaborator) => {
      const value = collaborator?._id ? collaborator?._id : collaborator.value;
      const label = collaborator?.label ? collaborator?.label : collaborator.profile.name;

      return {
        value,
        label,
      };
    });
    return data;
  };

  const getMinDate = () => {
    if (!currentSolutionRules.checkMinDate) {
      return null;
    }
    const today = moment().format('YYYY-MM-DD');
    if (serviceData?.start) {
      const currentServiceDate = moment(serviceData.start).format('YYYY-MM-DD');
      if (serviceDate < today && serviceDate === currentServiceDate) {
        return currentServiceDate;
      }
    }
    return today;
  };

  const getBaseData = useCallback(() => {
    return {
      start: new Date().toISOString(),
    };
  }, []);

  const setServiceData = useCallback(() => {
    const defaultValues = serviceData ?? getBaseData();
    const parsedStart = moment(defaultValues.start);
    const currentDate = moment();
    const startDate =
      currentSolutionRules.checkMinDate && parsedStart.isBefore(currentDate) ? currentDate : parsedStart;
    const servicePriority =
      Object.values(servicePriorities).find((priority) => priority.value === defaultValues.servicePriority) ??
      servicePriorities.NOT_PRIORITARY;

    const dataCollaborators = onHandleCollaborators(defaultValues?.collaborators);

    const formValues = {
      serviceDate: startDate.format('YYYY-MM-DD'),
      serviceTime: startDate.format('HH:mm'),
      serviceType: defaultValues.idTypeService,
      assignedCompany: defaultValues.idAssignedCompany,
      selectedAsset: defaultValues.idAsset?._id,
      selectedContact: defaultValues.idContactWhoCalls,
      selectedFinalCustomer: defaultValues.idFinalCustomer,
      serviceName: defaultValues.name,
      serviceDescription: defaultValues.title,
      collaborators: dataCollaborators,
      servicePriority,
      reference: defaultValues.reference,
      address: { address: defaultValues.address, markerCoords: defaultValues.markerCoords },
      origin: { address: defaultValues.from, markerCoords: defaultValues.markerFromCoords },
      selectedTechnician: defaultValues.idUser,
      headquarter: defaultValues.idHeadquarter,
      isScheduled: defaultValues.status === incidentsStatus.SCHEDULED,
      hoursBeforeAssignment: defaultValues.hoursBeforeAssignment,
    };
    form.setFieldsValue(formValues);
    onChangeValues(formValues);
  }, [serviceData, form, getBaseData, onChangeValues]);

  const selectFinalCustomer = (item) => {
    form.setFieldsValue({ selectedFinalCustomer: null });
    const newData = {
      selectedFinalCustomer: item,
    };
    if (currentSolutionRules.useFinalCustomerAddress && accountModules.finalCustomers) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }
    form.setFieldsValue(newData);
  };

  const checkCompanyAssignedToHeadquarter = (headquarter, company) => {
    let isCompanyAssigned = company == null;
    const headquarterServiceTypes = headquarter?.assignedServiceTypes;
    if (headquarterServiceTypes?.length && company) {
      isCompanyAssigned =
        company.isUsingAllServiceType ||
        company.servicesTypes?.some((idServiceType) => {
          return headquarter.assignedServiceTypes.some((serviceType) => serviceType._id === idServiceType);
        });
    }
    return isCompanyAssigned;
  };

  const checkServiceTypeAssignedToCompany = (serviceType, company) => {
    let isServiceTypeAssigned = company == null || company.isUsingAllServiceType;
    const companyServiceTypes = company?.servicesTypes;
    if (!isServiceTypeAssigned && companyServiceTypes?.length && serviceType) {
      isServiceTypeAssigned =
        company.isUsingAllServiceType ||
        companyServiceTypes.some(({ idServiceType }) => {
          return idServiceType === serviceType._id;
        });
    }
    return isServiceTypeAssigned;
  };

  const checkServiceTypeAssignedToHeadquarter = (serviceType, headquarter) => {
    let isServiceTypeAssigned = headquarter == null;
    const headquarterServiceTypes = headquarter?.assignedServiceTypes;
    if (headquarterServiceTypes?.length && serviceType) {
      isServiceTypeAssigned = headquarter.assignedServiceTypes.some((assignedServiceType) => {
        return assignedServiceType._id === serviceType._id;
      });
    }
    return isServiceTypeAssigned;
  };

  const getExcludedCollaborators = () => {
    const data = collaborators?.map((item) => item.value) ?? [];
    if (selectedTechnician) {
      data.push(selectedTechnician._id);
    }
    return data;
  };

  const checkTechnicianAssignedToServiceType = (technician, serviceType) => {
    const technicianSpecialities = technician?.profile.specialities?.map((item) => item?._id ?? item);
    const serviceTypeSpecialities = serviceType?.specialities?.map((item) => item?._id ?? item);
    let assignedToServiceType = serviceType == null;
    if (serviceTypeSpecialities?.length && technicianSpecialities?.length) {
      assignedToServiceType = technicianSpecialities.some((speciality) => serviceTypeSpecialities.includes(speciality));
    }
    return assignedToServiceType;
  };

  const checkTechnicianAssignedToHeadquarter = (technician, headquarter) => {
    const technicianHeadquarters = technician?.profile.idsHeadquarters;
    let assignedToHeadquarter = headquarter == null;
    if (technicianHeadquarters?.length && headquarter?._id) {
      assignedToHeadquarter = technicianHeadquarters.includes(headquarter._id);
    }
    return assignedToHeadquarter;
  };

  const selectHeadquarter = (item) => {
    const newData = {
      headquarter: item,
    };
    const currentTechnician = form.getFieldValue('selectedTechnician');
    const isCompanyAssigned = checkCompanyAssignedToHeadquarter(item, selectedCompany);
    const isServiceTypeAssignedToCompany = checkServiceTypeAssignedToCompany(selectedServiceType, selectedCompany);
    const isServiceTypeAssignedToHeadquarter = checkServiceTypeAssignedToHeadquarter(selectedServiceType, item);
    const isTechnicianAssignedToHeadquarter = checkTechnicianAssignedToHeadquarter(currentTechnician, item);
    const isTechnicianAssignedToServiceType = checkTechnicianAssignedToServiceType(
      currentTechnician,
      selectedServiceType,
    );
    if (currentSolutionRules.useHeadquartersAddress) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }

    if (!item?.assignedServiceTypes?.length || !isCompanyAssigned) {
      newData.assignedCompany = null;
      newData.selectedContact = null;
    }
    if (!isServiceTypeAssignedToHeadquarter || (isCompanyAssigned && !isServiceTypeAssignedToCompany)) {
      newData.serviceType = null;
    }
    if (
      !isTechnicianAssignedToHeadquarter ||
      (isCompanyAssigned && isServiceTypeAssignedToCompany && !isTechnicianAssignedToServiceType)
    ) {
      newData.selectedTechnician = null;
    }
    form.setFieldsValue(newData);
  };

  const selectCompany = (item) => {
    const newData = {
      assignedCompany: item,
      selectedContact: null,
    };
    const isServiceTypeAssignedToCompany = checkServiceTypeAssignedToCompany(selectedServiceType, item);
    if (!isServiceTypeAssignedToCompany) {
      newData.serviceType = null;
    }
    if (currentSolutionRules.useCompanyAddress && !accountModules.finalCustomers) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }
    form.setFieldsValue(newData);
  };

  const selectContact = (item) => {
    const newData = {};
    if (
      currentSolutionRules.useContactAddress &&
      (!currentSolutionRules.useFinalCustomerAddress || !accountModules.finalCustomers)
    ) {
      newData.address = {
        address: item?.address,
        markerCoords: item?.markerCoords,
      };
    }
    form.setFieldsValue(newData);
  };

  const selectServiceType = (item) => {
    const newData = {
      serviceType: item,
    };
    const currentTechnician = form.getFieldValue('selectedTechnician');
    const isTechnicianAssigned = checkTechnicianAssignedToServiceType(currentTechnician, item);
    if (!isTechnicianAssigned) {
      newData.selectedTechnician = null;
    }

    form.setFieldsValue(newData);
  };

  const onHandleAppointmentsRepeat = (event) => {
    setSwitchappointmentsRepeat(event);
  };

  const onHandleTechnician = (item) => {
    const currentCollaborators = form.getFieldValue('collaborators') || [];

    const updatedCollaborators = currentCollaborators.filter((collaborator) => collaborator.value !== item._id);

    form.setFieldsValue({ collaborators: updatedCollaborators });
  };

  const openModalTechniciansNear = () => {
    setShowModalTechniciansNear(true);
  };

  const handleSelectTechnicianNear = (technician) => {
    form.setFieldValue('selectedTechnician', technician);
    setShowModalTechniciansNear(false);
  };

  useEffect(() => {
    setServiceData();
  }, [setServiceData]);

  useEffect(() => {
    const newCollaborators = collaborators?.filter((item) => item?.value !== selectedTechnician?._id);
    form.setFieldsValue({
      collaborators: newCollaborators,
    });
  }, [selectedTechnician, collaborators]);

  return (
    <>
      <Form
        name={formName}
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        onValuesChange={onChangeValues}
        onFinishFailed={({ errorFields }) => {
          verifyFields(errorFields);
        }}
      >
        <Row>
          <Col xs={24} md={12}>
            <Form.Item rules={[{ required: true }]} name="headquarter" label={t('headquarter')}>
              <SelectHeadquarters value={selectedHeadquarter} onChange={selectHeadquarter} autoSelect={!isEditing} />
            </Form.Item>
          </Col>
          {selectedProfileRules.prioritizeReference || currentSolutionRules.prioritizeReference ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="reference"
                rules={[{ message: t('referenceRequired'), required: false }]}
                label={t('reference')}
              >
                <Input placeholder={t('referencePlaceholder')} />
              </Form.Item>
            </Col>
          ) : null}

          {currentSolutionRules.serviceName ? (
            <Col xs={24} md={24}>
              <Form.Item name="serviceName" label={t('serviceName')}>
                <Input placeholder={t('serviceNamePlaceholder')} />
              </Form.Item>
            </Col>
          ) : null}

          <Col xs={24} md={12}>
            <Form.Item
              name="serviceDate"
              rules={(() => {
                const validators = [{ message: t('serviceDateRequired'), required: true }];
                if (currentSolutionRules.checkMinDate) {
                  validators.push({
                    required: true,
                    validator: (_, date) => {
                      const minDate = getMinDate();
                      if (date >= minDate) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('minDateError', { minDate })));
                    },
                  });
                }
                return validators;
              })()}
              label={t('serviceDate')}
            >
              <Input placeholder={t('serviceDatePlaceholder')} type="date" min={getMinDate()} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="serviceTime"
              rules={(() => {
                const validators = [{ message: t('serviceTimeRequired'), required: true }];
                if (currentSolutionRules.checkMinDate) {
                  validators.push({
                    required: true,
                    validator: validateTime,
                  });
                }
                return validators;
              })()}
              label={t('serviceTime')}
              dependencies={['serviceDate']}
            >
              <Input placeholder={t('serviceTimePlaceholder')} type="time" />
            </Form.Item>
          </Col>

          {selectedProfileRules.showAddress && currentSolutionRules.showAddress ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="address"
                rules={[{ message: t('addressRequired'), required: true }]}
                label={t('address')}
              >
                <GooglePlacesAutocomplete />
              </Form.Item>
            </Col>
          ) : null}
          {accountModules.customers ? (
            <>
              <Col xs={24} md={12} z>
                <Form.Item
                  name="assignedCompany"
                  rules={[{ message: t('customerRequired'), required: true }]}
                  label={t('customer')}
                  valuePropName="selectedCompany"
                >
                  <SelectCompany onChange={selectCompany} idHeadquarter={selectedHeadquarter?._id} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="selectedContact"
                  rules={[{ message: t('contactRequired'), required: true }]}
                  label={t('contact')}
                  valuePropName="selectedContact"
                >
                  <SelectContact
                    onChange={selectContact}
                    selectedCompanyId={selectedCompany?._id}
                    enabledActions={currentSolutionRules?.enabledActionsContact}
                    selectedContact={selectContact}
                  />
                </Form.Item>
              </Col>
            </>
          ) : null}

          {currentSolutionRules.origin ? (
            <>
              {!selectedProfileRules.disableOrigin ? (
                <Col md={12} xs={12}>
                  <Form.Item style={{ margin: '0px' }} labelCol={{ span: 16 }} name="origin" label={t('origin')}>
                    <GooglePlacesAutocomplete label={t('origin')} />
                  </Form.Item>
                </Col>
              ) : null}

              {!selectedProfileRules.disableDestination ? (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="address"
                    rules={[{ message: t('destinationRequired'), required: true }]}
                    label={t('destination')}
                  >
                    <GooglePlacesAutocomplete label={t('destination')} />
                  </Form.Item>
                </Col>
              ) : null}
            </>
          ) : null}

          <Col xs={24} md={12}>
            <Form.Item
              name="serviceType"
              rules={[{ message: t('serviceTypeRequired'), required: true }]}
              label={t('serviceType')}
            >
              <SelectServiceType
                idHeadquarter={selectedHeadquarter?._id}
                idCustomer={selectedCompany?._id}
                onChange={selectServiceType}
                autoSelect
                selectedServiceType={selectedServiceType}
              />
            </Form.Item>
          </Col>
          {currentUser.profile?.idProfile !== profileTypes.TECHNICIAN.id ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="selectedTechnician"
                rules={[
                  {
                    message: t('sampleRequired', { sample: t(selectedProfileRules.profileData.name) }),
                    required: false,
                  },
                ]}
                label={t(selectedProfileRules.profileData.name)}
                valuePropName="selectedUser"
              >
                <SelectUser
                  autoselect
                  activeUsersOnly
                  allowCurrentUser={selectedProfileRules.allowSelectCurrentUser}
                  headquarter={selectedHeadquarter?._id}
                  specialties={selectedServiceType?.specialities}
                  idProfile={selectedProfileRules.profileData.id}
                  placeholder={t('sampleSelectPlaceholder', { sample: t(selectedProfileRules.profileData.name) })}
                  onChange={onHandleTechnician}
                  solutionId={solutionId}
                  openModalTechniciansNear={openModalTechniciansNear}
                />
              </Form.Item>
            </Col>
          ) : null}

          {currentSolutionRules.collaborators && !selectedProfileRules.disableCollaborators ? (
            <Col xs={24} md={12}>
              <Form.Item name="collaborators" valuePropName="selectedUser" label={t('collaborators')}>
                <SelectUser
                  allowCurrentUser={selectedProfileRules.allowSelectCurrentUser}
                  headquarter={selectedHeadquarter?._id}
                  specialties={selectedServiceType?.specialities}
                  idProfile={selectedProfileRules.profileData.id}
                  placeholder={t('collaboratorsPlaceholder')}
                  multiple
                  excludedIds={getExcludedCollaborators()}
                />
              </Form.Item>
            </Col>
          ) : null}
          {selectedProfileRules.showPriority ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="servicePriority"
                rules={[{ message: t('priorityRequired'), required: true }]}
                label={t('priority')}
                initialValue={servicePriorities.NOT_PRIORITARY}
              >
                <SelectPriority />
              </Form.Item>
            </Col>
          ) : null}
          {!selectedProfileRules.prioritizeReference && !currentSolutionRules.prioritizeReference ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="reference"
                rules={[{ message: t('referenceRequired'), required: false }]}
                label={t('reference')}
              >
                <Input placeholder={t('referencePlaceholder')} />
              </Form.Item>
            </Col>
          ) : null}
          {showScheduledSection ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="isScheduled"
                label={t('isServiceTypeScheduled', { name: t('service') })}
                valuePropName="checked"
              >
                <CustomSwitch checkedText={t('yes')} unCheckedText={t('no')} />
              </Form.Item>
            </Col>
          ) : null}
          {showScheduledSection && isScheduled ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="hoursBeforeAssignment"
                label={t('hoursBeforeChangeToAssigned')}
                rules={[
                  { required: true, message: t('fieldRequired') },
                  { type: 'integer', min: 1, max: 1000, message: t('fieldRequired') },
                ]}
              >
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          ) : null}
          {isEditing ? null : selectedProfileRules.showCheckRepeatAppointment ? (
            <Col xs={24}>
              <Row>
                <Col xs={6}>
                  <Form.Item
                    name="checkRepeatAppointment"
                    label={t('appointmentRepeat')}
                    style={{ marginBottom: '0px' }}
                  >
                    <CustomSwitch
                      checked={switchappointmentsRepeat}
                      onChange={onHandleAppointmentsRepeat}
                      checkedText={t('yes')}
                      unCheckedText={t('no')}
                    />
                  </Form.Item>
                </Col>
                {switchappointmentsRepeat && <FormRepeatAppointments />}
              </Row>
            </Col>
          ) : null}

          {accountModules.finalCustomers && selectedServiceType?.allowFinalCustomerAssignment ? (
            <>
              <Col xs={24}>
                <FinalCustomerInput
                  name="selectedFinalCustomer"
                  value={selectedFinalCustomer}
                  onChange={selectFinalCustomer}
                  enableDropdown={selectedProfileRules.enableDropdown}
                  showServicesCount
                  required={currentSolutionRules.finalCustomerRequired || selectedProfileRules.finalCustomerRequired}
                />
              </Col>
              {accountModules.asset &&
              selectedServiceType?.allowFinalCustomerAssetAssignment &&
              selectedServiceType?.finalCustomerAssetTypeData &&
              selectedFinalCustomer ? (
                <Col span={24}>
                  <Col span={24}>
                    <h3 style={{ paddingLeft: '11px' }}>
                      <b>{selectedServiceType.finalCustomerAssetTypeData.name}</b>
                    </h3>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="selectedAsset"
                      valuePropName="selectedAssetValue"
                      rules={[{ message: t('fieldRequired'), required: true }]}
                    >
                      <AssetsByFinalCustomerButtons
                        assetTypeId={selectedServiceType.idFinalCustomerAssetType}
                        finalCustomerId={selectedFinalCustomer._id}
                        finalCustomerAssetTypeData={selectedServiceType.finalCustomerAssetTypeData}
                      />
                    </Form.Item>
                  </Col>
                </Col>
              ) : null}
            </>
          ) : null}

          <Col xs={24} md={24}>
            <Form.Item name="serviceDescription" label={t('serviceDescription')}>
              <Input.TextArea autoSize={{ minRows: 2 }} placeholder={t('serviceDescriptionPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        {showModalTechniciansNear && (
          <ModalTechniciansNear
            handleClose={() => {
              setShowModalTechniciansNear(false);
            }}
            show={showModalTechniciansNear}
            headquarter={form.getFieldValue('headquarter')?._id}
            specialities={form.getFieldValue('serviceType')?.specialities}
            address={form.getFieldValue('address')}
            handleSelectTechnician={handleSelectTechnicianNear}
            technicianAssigned={form.getFieldValue('selectedTechnician')}
          />
        )}
      </Form>
    </>
  );
};

FormEditService.propTypes = {
  serviceData: PropTypes.shape({
    start: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    reference: PropTypes.string,
    address: PropTypes.string,
    idUser: PropTypes.object,
    idTypeService: PropTypes.object,
    idAssignedCompany: PropTypes.object,
    selectedAsset: PropTypes.string,
    idContactWhoCalls: PropTypes.object,
    idFinalCustomer: PropTypes.object,
    collaborators: PropTypes.array,
    status: PropTypes.number,
    hoursBeforeAssignment: PropTypes.number,
  }),
  formName: PropTypes.string,
  handleSubmit: PropTypes.func,
  onChangeValues: PropTypes.func,
  isEditing: PropTypes.bool,
  selectedProfile: PropTypes.string,
};
